import type {
  DisclosureFormLine,
  DisclosureListItem,
  DisclosureStatus,
  DisclosureView,
  LoanFile,
} from "./disclosure";
import type {
  Activity,
  ConditionItem,
  ConditionOwner,
  ConditionProject,
  DashboardData,
  Deduction,
  LoanOfficer,
  MemberInvitation,
  MembersData,
  PayrollField,
  PayrollLoan,
  PayrollRun,
  ProjectSummary,
  User,
  WorkStatus,
} from "./types";

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

const value = (input: any, ...keys: string[]): any => {
  for (const key of keys) {
    if (input?.[key] !== undefined && input?.[key] !== null) {
      return input[key];
    }
  }
  return undefined;
};

const text = (input: unknown, fallback = ""): string =>
  typeof input === "string" || typeof input === "number" ? String(input) : fallback;

const number = (input: unknown, fallback = 0): number => {
  const parsed = typeof input === "string" ? Number(input) : input;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : fallback;
};

const bool = (input: unknown, fallback = false): boolean => {
  if (typeof input === "boolean") {
    return input;
  }
  if (typeof input === "string") {
    return input === "true" || input === "1";
  }
  if (typeof input === "number") {
    return input !== 0;
  }
  return fallback;
};

const list = (input: any, ...keys: string[]): any[] => {
  if (Array.isArray(input)) {
    return input;
  }
  for (const key of keys) {
    if (Array.isArray(input?.[key])) {
      return input[key];
    }
  }
  if (input?.data && typeof input.data === "object") {
    return list(input.data, ...keys);
  }
  return [];
};

const entity = (input: any, ...keys: string[]): any => {
  for (const key of keys) {
    if (input?.[key] && typeof input[key] === "object") {
      return input[key];
    }
  }
  if (input?.data && typeof input.data === "object" && !Array.isArray(input.data)) {
    return entity(input.data, ...keys);
  }
  return input ?? {};
};

let csrfToken: string | undefined;
let sessionToken: string | undefined;

const SESSION_STORAGE_KEY = "candr_session_token";
const CSRF_STORAGE_KEY = "candr_csrf_token";

/** Empty in local Vite dev (proxy /api). Absolute API origin in split deploys. */
const API_BASE = String(import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const FETCH_CREDENTIALS: RequestCredentials = API_BASE ? "include" : "same-origin";
const SPLIT_HOST = Boolean(API_BASE);

function apiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function cookie(name: string): string | undefined {
  const target = `${encodeURIComponent(name)}=`;
  return document.cookie
    .split("; ")
    .find((part) => part.startsWith(target))
    ?.slice(target.length);
}

function readStorage(key: string): string | undefined {
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStorage(key: string, stored: string | undefined) {
  try {
    if (!stored) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, stored);
  } catch {
    // Private mode / blocked storage — in-memory still works for this tab.
  }
}

function loadPersistedAuth() {
  if (!sessionToken) {
    sessionToken = readStorage(SESSION_STORAGE_KEY);
  }
  if (!csrfToken) {
    csrfToken = readStorage(CSRF_STORAGE_KEY);
  }
}

function persistAuth(nextSession?: string, nextCsrf?: string) {
  if (nextSession !== undefined) {
    sessionToken = nextSession || undefined;
    writeStorage(SESSION_STORAGE_KEY, sessionToken);
  }
  if (nextCsrf !== undefined) {
    csrfToken = nextCsrf || undefined;
    writeStorage(CSRF_STORAGE_KEY, csrfToken);
  }
}

function clearAuth() {
  sessionToken = undefined;
  csrfToken = undefined;
  writeStorage(SESSION_STORAGE_KEY, undefined);
  writeStorage(CSRF_STORAGE_KEY, undefined);
}

loadPersistedAuth();

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  loadPersistedAuth();
  const method = init.method?.toUpperCase() ?? "GET";
  const csrf = csrfToken ?? cookie("candr_csrf") ?? cookie("csrf_token") ?? cookie("csrftoken");
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  // Safari ITP often drops cross-site cookies between Pages and Railway.
  // Prefer explicit Bearer when we have a session token from login JSON.
  if (sessionToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${sessionToken}`);
  }
  if (csrf && !["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("X-CSRF-Token", csrf);
  }

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...init,
      headers,
      credentials: FETCH_CREDENTIALS,
    });
  } catch {
    throw new ApiError("Could not reach the C&R server. Check your connection and try again.", 0);
  }

  if (response.status === 401 && SPLIT_HOST) {
    clearAuth();
  }

  if (response.status === 204) {
    return undefined as T;
  }
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json") || contentType.includes("+json");
  const body = isJson
    ? await response.json().catch(() => undefined)
    : await response.text().catch(() => "");

  if (!response.ok) {
    const nestedError = body && typeof body === "object" ? value(body, "error") : undefined;
    const message =
      text(value(body, "message", "detail")) ||
      text(value(nestedError, "message", "detail")) ||
      (response.status === 401
        ? "Your session has expired. Please sign in again."
        : response.status === 404 && path.startsWith("/api")
          ? "API is not configured for this deployment (set VITE_API_BASE_URL)."
          : `Request failed (${response.status}).`);
    throw new ApiError(message, response.status, body);
  }

  // SPA hosts must never treat HTML/text 200s as successful API payloads.
  if (!isJson) {
    throw new ApiError(
      "API returned a non-JSON response. Set VITE_API_BASE_URL to the Railway API origin.",
      response.status || 502,
      body,
    );
  }
  return body as T;
}

function normalizeStatus(input: unknown): WorkStatus {
  const raw = text(input, "draft").toLowerCase().replaceAll("-", "_").replaceAll(" ", "_");
  if (raw.includes("complete") || raw === "completed" || raw === "approved" || raw === "snapshot") {
    return "complete";
  }
  if (raw.includes("review") || raw.includes("attention")) {
    return "needs_review";
  }
  if (raw.includes("process") || raw.includes("extract")) {
    return "processing";
  }
  if (raw.includes("wait") || raw.includes("investor")) {
    return "waiting";
  }
  return "draft";
}

function normalizeKind(input: any): "conditions" | "payroll" {
  const raw = text(value(input, "kind", "type", "project_type", "workflow")).toLowerCase();
  return raw.includes("pay") || raw.includes("commission") ? "payroll" : "conditions";
}

function normalizeProject(input: any): ProjectSummary {
  const kind = normalizeKind(input);
  return {
    id: text(value(input, "id", "project_id", "condition_id", "payroll_id")),
    workflowId: text(value(input, "workflow_id", "workflowId")) || undefined,
    kind,
    title:
      text(value(input, "title", "name", "borrower_name", "borrowerName")) ||
      (kind === "payroll" ? "Payroll review" : "Conditions review"),
    subtitle:
      text(value(input, "subtitle", "loan_number", "loanNumber", "property_address")) ||
      (kind === "payroll" ? "Loan payroll" : "Underwriting conditions"),
    status: normalizeStatus(value(input, "status", "state")),
    updatedAt: text(value(input, "updated_at", "updatedAt", "created_at", "createdAt")),
    owner: text(value(input, "owner_name", "owner", "created_by_name"), "C&R Operations"),
    progress: number(value(input, "progress", "percent_complete"), undefined as unknown as number),
  };
}

function normalizeActivity(input: any): Activity {
  return {
    id: text(value(input, "id", "event_id"), crypto.randomUUID()),
    actor: text(value(input, "actor_name", "actor", "user_name"), "C&R Operations"),
    action: text(value(input, "action", "verb", "event"), "updated"),
    subject: text(value(input, "subject", "object_name", "description"), "a project"),
    at: text(value(input, "created_at", "createdAt", "at", "timestamp")),
  };
}

function normalizeOwner(input: unknown): ConditionOwner {
  const raw = text(input).toLowerCase();
  if (raw.includes("agent") || raw.includes("realtor")) {
    return "agent";
  }
  if (raw.includes("borrower") || raw.includes("buyer")) {
    return "borrower";
  }
  return "internal";
}

function normalizeCondition(input: any, index: number): ConditionItem {
  const rawOwner = text(
    value(input, "owner", "party", "category", "internal_owner", "internalOwner"),
  );
  const owner = normalizeOwner(value(input, "communication_group", "owner", "party", "category"));
  const urgencyRaw = text(value(input, "urgency", "priority")).toLowerCase();
  const isUrgent = bool(value(input, "urgent"));
  const urgency =
    isUrgent || urgencyRaw.includes("urgent") || urgencyRaw.includes("high")
      ? "urgent"
      : urgencyRaw.includes("soon") || urgencyRaw.includes("medium")
        ? "soon"
        : "standard";
  return {
    id: text(value(input, "id", "condition_id"), `condition-${index + 1}`),
    number: text(value(input, "number", "condition_number", "sequence"), String(index + 1)),
    title: text(value(input, "title", "summary", "label"), `Condition ${index + 1}`),
    sourceText: text(value(input, "source_text", "sourceText", "description", "text")),
    owner,
    internalOwner:
      owner === "internal"
        ? text(
            value(input, "internal_owner", "internalOwner", "assignee"),
            rawOwner || "processing",
          )
        : undefined,
    status: text(value(input, "status")).toLowerCase().includes("satisf")
      ? "satisfied"
      : text(value(input, "status")).toLowerCase().includes("review")
        ? "in_review"
        : "open",
    urgency,
    requiresESign: bool(
      value(input, "requires_esign", "requiresESign", "e_sign", "esign", "needs_esign"),
    ),
    sensitive: bool(value(input, "sensitive", "contains_sensitive_data", "pii")),
    confidence: number(value(input, "confidence", "extraction_confidence"), 0),
    source: text(value(input, "source", "source_location", "page")),
    dependencies: list(value(input, "dependencies", "depends_on"))
      .map((item) => text(item))
      .filter(Boolean),
  };
}

function normalizeDrafts(input: any): ConditionProject["drafts"] {
  const drafts = entity(value(input, "drafts", "email_drafts") ?? {});
  const body = (draft: unknown): string =>
    draft && typeof draft === "object"
      ? text(value(draft, "body", "text", "content"))
      : text(draft);
  return {
    borrower: body(value(drafts, "borrower", "borrower_email", "borrowerEmail")),
    agent: body(value(drafts, "agent", "agent_email", "agentEmail", "realtor")),
    internal: body(value(drafts, "internal", "internal_email", "internalEmail", "processing")),
  };
}

function normalizeConditionProject(raw: any): ConditionProject {
  const input = entity(raw, "condition", "project", "item");
  const conditions = list(input, "conditions", "items", "extracted_conditions").map(
    normalizeCondition,
  );
  return {
    id: text(value(input, "id", "project_id", "condition_id")),
    title: text(value(input, "title", "name", "borrower_name"), "Conditions review"),
    borrowerName: text(value(input, "borrower_name", "borrowerName", "borrower")),
    loanNumber: text(value(input, "loan_number", "loanNumber")),
    propertyAddress: text(value(input, "property_address", "propertyAddress", "property")),
    status: normalizeStatus(value(input, "status", "state")),
    createdAt: text(value(input, "created_at", "createdAt")),
    updatedAt: text(value(input, "updated_at", "updatedAt", "created_at")),
    fileName: text(value(input, "file_name", "fileName", "source_document_name", "source_name")),
    conditionCount: number(value(input, "condition_count", "conditionCount"), conditions.length),
    conditions,
    drafts: normalizeDrafts(input),
    internalSummary: text(
      value(input, "internal_summary", "internalSummary", "summary"),
      text(value(entity(value(input, "drafts") ?? {}), "summary")),
    ),
  };
}

function normalizePayrollField(input: any, index: number): PayrollField {
  const provenance = entity(value(input, "provenance", "source") ?? {});
  const rawConflict = value(input, "conflict");
  const conflict = rawConflict && typeof rawConflict === "object" ? entity(rawConflict) : undefined;
  return {
    key: text(value(input, "key", "name", "field"), `field-${index}`),
    label: text(value(input, "label", "display_name", "name"), `Field ${index + 1}`),
    value: text(value(input, "value", "extracted_value")),
    required: bool(value(input, "required")),
    provenance: value(input, "provenance", "source")
      ? {
          source: text(
            value(provenance, "source", "document", "name"),
            text(value(input, "source")),
          ),
          location: text(
            value(input, "location", "page", "area"),
            text(value(provenance, "location", "page", "area")),
          ),
          confidence: number(value(provenance, "confidence"), number(value(input, "confidence"))),
        }
      : undefined,
    conflict: conflict
      ? {
          value: text(value(conflict, "value", "alternate_value")),
          source: text(value(conflict, "source", "document")),
        }
      : undefined,
  };
}

function normalizeDeduction(input: any, index: number): Deduction {
  const cents = value(input, "amount_cents", "amountCents");
  return {
    id: text(value(input, "id", "deduction_id"), `deduction-${index}`),
    label: text(value(input, "label", "name", "description"), "Deduction"),
    amountCents:
      cents === undefined ? Math.round(number(value(input, "amount")) * 100) : number(cents),
    checked: bool(value(input, "checked", "enabled", "selected"), true),
    recurring: bool(value(input, "recurring", "is_recurring")),
  };
}

function normalizePayroll(raw: any): PayrollLoan {
  const input = entity(raw, "payroll", "loan", "item");
  const statusRaw = text(
    value(input, "investor_status", "investorStatus", "purchase_status"),
  ).toLowerCase();
  const explicitEligibility = value(input, "eligible");
  const eligible =
    explicitEligibility !== undefined
      ? bool(explicitEligibility)
      : bool(value(input, "investor_purchased", "purchased")) || statusRaw.includes("eligible");
  const methodRaw = text(
    value(input, "comp_method", "compMethod", "calculation_method"),
    "flat_fee",
  );
  const methodAliases: Record<string, PayrollLoan["compMethod"]> = {
    lock_price: "lock_price_as_is",
    percent_lock_price: "lock_price_percentage",
    percent_loan_amount: "loan_amount_percentage",
  };
  const supportedMethods = new Set([
    "lock_price_percentage",
    "loan_amount_percentage",
    "basis_points",
    "flat_fee",
    "lock_price_as_is",
  ]);
  const conflictRecords = list(input, "conflicts");
  const fields = list(input, "fields", "extracted_fields", "review_fields")
    .map(normalizePayrollField)
    .map((field) => {
      const conflict = conflictRecords.find(
        (item) => text(value(item, "key", "field")) === field.key,
      );
      return conflict
        ? {
            ...field,
            conflict: {
              value: text(value(conflict, "purchase_advice", "alternate_value")),
              source: "Purchase Advice",
            },
          }
        : field;
    });
  return {
    id: text(value(input, "id", "payroll_id", "loan_id")),
    title: text(value(input, "title", "name", "borrower_name"), "Payroll review"),
    borrowerName: text(value(input, "borrower_name", "borrowerName", "borrower")),
    loanNumber: text(value(input, "loan_number", "loanNumber")),
    loanOfficer: text(value(input, "loan_officer_name", "loanOfficer", "loan_officer")),
    status: normalizeStatus(value(input, "status", "state")),
    investorStatus: eligible ? "eligible" : "waiting",
    purchaseStatus: statusRaw.includes("purchased") ? "purchased" : "waiting",
    investorName: text(value(input, "investor_name", "investorName", "investor")),
    purchaseDate: text(value(input, "purchase_date", "purchaseDate")),
    createdAt: text(value(input, "created_at", "createdAt")),
    updatedAt: text(value(input, "updated_at", "updatedAt", "created_at")),
    files: list(input, "files", "documents").map((file: any) => ({
      name: text(value(file, "name", "file_name", "filename"), "Document"),
      type: text(value(file, "type", "document_type"))
        .toLowerCase()
        .includes("purchase")
        ? "purchase_advice"
        : "closing_disclosure",
    })),
    fields,
    deductions: list(input, "deductions", "fees").map(normalizeDeduction),
    compMethod:
      methodAliases[methodRaw] ??
      (supportedMethods.has(methodRaw) ? (methodRaw as PayrollLoan["compMethod"]) : "flat_fee"),
    compValue: text(value(input, "comp_value", "compValue", "rate", "basis_points")),
    grossCents:
      value(input, "gross_cents", "grossCents", "gross_amount_cents") !== undefined
        ? number(value(input, "gross_cents", "grossCents", "gross_amount_cents"))
        : Math.round(number(value(input, "gross_comp")) * 100),
    deductionCents:
      value(input, "deduction_cents", "deductionCents", "deductions_cents") !== undefined
        ? number(value(input, "deduction_cents", "deductionCents", "deductions_cents"))
        : Math.round(number(value(input, "deduction_total")) * 100),
    netCents:
      value(input, "net_cents", "netCents", "payroll_total_cents") !== undefined
        ? number(value(input, "net_cents", "netCents", "payroll_total_cents"))
        : Math.round(number(value(input, "net_comp")) * 100),
  };
}

function normalizeRun(input: any): PayrollRun {
  const included = value(input, "included");
  const excluded = value(input, "excluded");
  return {
    id: text(value(input, "id", "run_id")),
    name: text(value(input, "name", "title"), "Payroll run"),
    status: text(value(input, "status"), "complete"),
    createdAt: text(value(input, "created_at", "createdAt")),
    eligibleCount: Array.isArray(included)
      ? included.length
      : number(value(input, "eligible_count", "included_count", "eligibleCount", "included")),
    waitingCount: Array.isArray(excluded)
      ? excluded.length
      : number(value(input, "waiting_count", "excluded_count", "waitingCount", "excluded")),
    grossCents: number(value(input, "gross_cents", "grossCents")),
    deductionsCents: number(value(input, "deductions_cents", "deductionsCents")),
    netCents:
      value(input, "net_cents", "netCents", "total_cents") !== undefined
        ? number(value(input, "net_cents", "netCents", "total_cents"))
        : Math.round(number(value(input, "total")) * 100),
  };
}

export const api = {
  async login(email: string, password: string): Promise<User> {
    const response = await request<any>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    const nextCsrf = text(value(response, "csrf_token", "csrfToken"));
    const nextSession = text(value(response, "session_token", "sessionToken"));
    persistAuth(nextSession || undefined, nextCsrf || undefined);
    return this.normalizeUser(response);
  },

  async me(): Promise<User> {
    const response = await request<any>("/api/auth/me");
    const nextCsrf = text(value(response, "csrf_token", "csrfToken"));
    if (nextCsrf) {
      persistAuth(undefined, nextCsrf);
    }
    return this.normalizeUser(response);
  },

  normalizeUser(raw: any): User {
    const input = entity(raw, "user", "account");
    const email = text(value(input, "email"));
    return {
      id: text(value(input, "id", "user_id")),
      name: text(
        value(input, "name", "display_name", "full_name"),
        email.split("@")[0] || "C&R User",
      ),
      email,
      role: text(value(input, "role", "role_name"), "Operations"),
      organizationName: text(
        value(input, "organization_name", "organizationName", "company_name", "company"),
        "C&R Mortgage Group",
      ),
      isDemo: bool(value(input, "is_demo", "isDemo")) || email === "demo@candrmortgage.com",
    };
  },

  async logout(): Promise<void> {
    try {
      await request<void>("/api/auth/logout", { method: "POST" });
    } finally {
      clearAuth();
    }
  },

  async dashboard(): Promise<DashboardData> {
    const raw = await request<any>("/api/dashboard");
    const input = entity(raw, "dashboard");
    const projects = list(input, "projects", "items");
    const activeSource = list(input, "active", "active_projects", "current_work");
    const recentSource = list(input, "recent", "recent_projects", "history", "completed");
    const active = (
      activeSource.length > 0
        ? activeSource
        : projects.filter((item) => normalizeStatus(value(item, "status")) !== "complete")
    ).map(normalizeProject);
    const recent = (
      recentSource.length > 0
        ? recentSource
        : projects.filter((item) => normalizeStatus(value(item, "status")) === "complete")
    ).map(normalizeProject);
    const counts = entity(value(input, "counts", "stats", "metrics") ?? {});
    return {
      active,
      recent,
      activity: list(input, "activity", "activities", "recent_activity", "events").map(
        normalizeActivity,
      ),
      counts: {
        conditionsReview: number(
          value(
            counts,
            "conditions_review",
            "conditionsReview",
            "conditions_needing_review",
            "active_conditions",
          ),
          active.filter((item) => item.kind === "conditions").length,
        ),
        loansReview: number(value(counts, "loans_in_review", "loansReview")),
        payrollEligible: number(
          value(counts, "payroll_eligible", "payrollEligible", "eligible_loans", "payroll_ready"),
        ),
        waitingInvestor: number(
          value(counts, "waiting_investor", "waitingInvestor", "waiting_loans"),
        ),
        completedMonth: number(value(counts, "completed_month", "completedMonth", "completed")),
      },
    };
  },

  async projects(): Promise<ProjectSummary[]> {
    const raw = await request<any>("/api/projects");
    return list(raw, "projects", "items", "data").map(normalizeProject);
  },

  async project(id: string): Promise<ProjectSummary> {
    return normalizeProject(
      entity(await request(`/api/projects/${encodeURIComponent(id)}`), "project", "item"),
    );
  },

  async conditions(): Promise<ConditionProject[]> {
    const raw = await request<any>("/api/conditions");
    return list(raw, "conditions", "projects", "items", "data").map(normalizeConditionProject);
  },

  async createCondition(input: {
    file?: File;
    pastedText?: string;
    borrowerName?: string;
    loanNumber?: string;
    propertyAddress?: string;
  }): Promise<ConditionProject> {
    const body = new FormData();
    if (input.file) {
      body.set("file", input.file);
    }
    if (input.pastedText) {
      body.set("text", input.pastedText);
    }
    if (input.borrowerName) {
      body.set("borrower_name", input.borrowerName);
    }
    if (input.loanNumber) {
      body.set("loan_number", input.loanNumber);
    }
    if (input.propertyAddress) {
      body.set("property_address", input.propertyAddress);
    }
    return normalizeConditionProject(await request("/api/conditions", { method: "POST", body }));
  },

  async condition(id: string): Promise<ConditionProject> {
    return normalizeConditionProject(await request(`/api/conditions/${encodeURIComponent(id)}`));
  },

  async updateCondition(id: string, patch: Partial<ConditionProject>): Promise<ConditionProject> {
    const payload: Record<string, unknown> = {};
    if (patch.status) {
      payload.status =
        patch.status === "complete"
          ? "completed"
          : patch.status === "draft"
            ? "draft"
            : "in_review";
    }
    if (patch.conditions) {
      payload.conditions = patch.conditions.map((item) => ({
        id: item.id,
        source_text: item.sourceText,
        owner: item.owner === "internal" ? item.internalOwner || "processing" : item.owner,
        communication_group: item.owner,
        internal_owner: item.internalOwner,
        urgent: item.urgency === "urgent",
        urgency: item.urgency === "standard" ? "normal" : item.urgency,
        needs_esign: item.requiresESign,
        sensitive: item.sensitive,
        dependencies: item.dependencies,
        confidence: item.confidence,
        page: item.source,
        status:
          patch.status === "complete" || item.status === "satisfied"
            ? "confirmed"
            : "review_required",
        title: item.title,
        number: item.number,
      }));
    }
    if (patch.drafts || patch.internalSummary !== undefined) {
      payload.drafts = {
        borrower:
          patch.drafts?.borrower !== undefined ? { body: patch.drafts.borrower } : undefined,
        agent: patch.drafts?.agent !== undefined ? { body: patch.drafts.agent } : undefined,
        internal:
          patch.drafts?.internal !== undefined ? { body: patch.drafts.internal } : undefined,
        summary: patch.internalSummary,
      };
    }
    const response = await request<any>(`/api/conditions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return response ? normalizeConditionProject(response) : this.condition(id);
  },

  async payroll(): Promise<PayrollLoan[]> {
    const raw = await request<any>("/api/payroll");
    return list(raw, "payroll", "loans", "items", "data").map(normalizePayroll);
  },

  async createPayroll(input: {
    closingDisclosure: File;
    purchaseAdvice?: File;
  }): Promise<PayrollLoan> {
    const body = new FormData();
    body.set("closing_disclosure", input.closingDisclosure);
    if (input.purchaseAdvice) {
      body.set("purchase_advice", input.purchaseAdvice);
    }
    return normalizePayroll(await request("/api/payroll", { method: "POST", body }));
  },

  async payrollLoan(id: string): Promise<PayrollLoan> {
    return normalizePayroll(await request(`/api/payroll/${encodeURIComponent(id)}`));
  },

  async updatePayroll(id: string, patch: Partial<PayrollLoan>): Promise<PayrollLoan> {
    const payload: Record<string, unknown> = {};
    if (patch.fields) {
      const editableKeys: Record<string, string> = {
        borrower: "borrower",
        loan_number: "loan_number",
        lender: "lender",
        investor: "investor",
        loan_amount: "loan_amount",
        buy_price: "lock_price",
        lock_price: "lock_price",
        interest_rate: "interest_rate",
        closing_date: "closing_date",
        purchase_date: "purchase_date",
      };
      for (const field of patch.fields) {
        const target = editableKeys[field.key];
        if (target) {
          payload[target] = field.value;
        }
      }
    }
    if (patch.deductions) {
      payload.deductions = patch.deductions.map((item) => ({
        id: item.id,
        label: item.label,
        amount: item.amountCents / 100,
        enabled: item.checked,
      }));
    }
    if (patch.compMethod) {
      payload.comp_method = patch.compMethod;
    }
    if (patch.compValue !== undefined) {
      payload.comp_value = patch.compValue;
    }
    if (patch.status === "complete") {
      payload.financial_review_confirmed = true;
      payload.deductions_confirmed = true;
      payload.compliance_warning_acknowledged = true;
    }
    if (patch.purchaseStatus) {
      payload.purchase_status =
        patch.purchaseStatus === "purchased" ? "purchased" : "waiting_on_investor";
    }
    if (patch.loanOfficer) {
      payload.loan_officer = patch.loanOfficer;
    }
    const response = await request<any>(`/api/payroll/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return response ? normalizePayroll(response) : this.payrollLoan(id);
  },

  async createPayrollRun(payrollIds: string[]): Promise<PayrollRun> {
    return normalizeRun(
      entity(
        await request("/api/payroll/runs", {
          method: "POST",
          body: JSON.stringify({ loan_ids: payrollIds }),
        }),
        "run",
      ),
    );
  },

  async downloadRun(id: string): Promise<void> {
    loadPersistedAuth();
    const headers = new Headers({ Accept: "*/*" });
    if (sessionToken) {
      headers.set("Authorization", `Bearer ${sessionToken}`);
    }
    const response = await fetch(apiUrl(`/api/payroll/runs/${encodeURIComponent(id)}/export`), {
      credentials: FETCH_CREDENTIALS,
      headers,
    });
    if (!response.ok) {
      throw new ApiError("The payroll workbook could not be exported.", response.status);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") ?? "";
    const fileName = disposition.match(/filename="?([^"]+)"?/i)?.[1] ?? `payroll-run-${id}.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  },

  async loanOfficers(): Promise<LoanOfficer[]> {
    try {
      const raw = await request<any>("/api/settings/loan-officers");
      return list(raw, "loan_officers", "loanOfficers", "items", "data").map((input: any) => ({
        id: text(value(input, "id", "loan_officer_id")),
        name: text(value(input, "name", "full_name")),
        email: text(value(input, "email")),
        title: text(value(input, "title", "role"), "Loan Officer"),
        defaultMethod: text(
          value(input, "default_method", "defaultMethod"),
          "basis_points",
        ) as LoanOfficer["defaultMethod"],
        defaultValue: text(value(input, "default_value", "defaultValue", "default_rate", "rate")),
        active: bool(value(input, "active", "is_active"), true),
      }));
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return [];
      }
      throw error;
    }
  },

  async members(): Promise<MembersData> {
    const raw = await request<any>("/api/members");
    const member = (input: any) => ({
      id: text(value(input, "id")),
      name: text(value(input, "name", "display_name")),
      email: text(value(input, "email")),
      role: text(value(input, "role"), "Member"),
      accessProfile: text(value(input, "access_profile", "accessProfile"), "Workspace member"),
      status: text(value(input, "status"), "active"),
      createdAt: text(value(input, "created_at", "createdAt")),
    });
    const invitation = (input: any): MemberInvitation => ({
      id: text(value(input, "id")),
      name: text(value(input, "name", "display_name")),
      email: text(value(input, "email")),
      accessProfile: text(value(input, "access_profile", "accessProfile"), "Workspace member"),
      status: text(value(input, "status"), "pending"),
      expiresAt: text(value(input, "expires_at", "expiresAt")),
      createdAt: text(value(input, "created_at", "createdAt")),
    });
    const access = entity(value(raw, "access_model", "accessModel") ?? {});
    return {
      members: list(raw, "members").map(member),
      invitations: list(raw, "invitations").map(invitation),
      accessModel: {
        name: text(value(access, "name"), "Workspace member"),
        summary: text(
          value(access, "summary"),
          "All members currently have the same workspace access.",
        ),
        rulesVersion: number(value(access, "rules_version", "rulesVersion"), 1),
      },
    };
  },

  async inviteMember(input: { name: string; email: string }): Promise<MemberInvitation> {
    const raw = await request<any>("/api/members", {
      method: "POST",
      body: JSON.stringify(input),
    });
    const invitation = entity(raw, "invitation");
    return {
      id: text(value(invitation, "id")),
      name: text(value(invitation, "name", "display_name")),
      email: text(value(invitation, "email")),
      accessProfile: text(value(invitation, "access_profile", "accessProfile"), "Workspace member"),
      status: text(value(invitation, "status"), "pending"),
      expiresAt: text(value(invitation, "expires_at", "expiresAt")),
      createdAt: text(value(invitation, "created_at", "createdAt")),
      invitePath: text(value(raw, "invite_path", "invitePath")) || undefined,
    };
  },

  async acceptInvitation(input: { token: string; password: string }): Promise<{
    email: string;
    name: string;
  }> {
    const raw = await request<any>("/api/auth/invitations/accept", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return {
      email: text(value(raw, "email")),
      name: text(value(raw, "name")),
    };
  },

  async disclosures(): Promise<{
    items: DisclosureListItem[];
    signnowConfigured: boolean;
  }> {
    const raw = await request<any>("/api/disclosures");
    return {
      items: list(raw, "items").map(normalizeDisclosureItem),
      signnowConfigured: bool(value(raw, "signnow_configured", "signnowConfigured")),
    };
  },

  async disclosure(id: string): Promise<DisclosureView> {
    return normalizeDisclosure(await request(`/api/disclosures/${encodeURIComponent(id)}`));
  },

  async previewDisclosure(file: LoanFile): Promise<DisclosureView> {
    return normalizeDisclosure(
      await request("/api/disclosures/preview", {
        method: "POST",
        body: JSON.stringify({ file }),
      }),
    );
  },

  async importDisclosureXml(xml: string): Promise<DisclosureView> {
    return normalizeDisclosure(
      await request("/api/disclosures/import", {
        method: "POST",
        body: JSON.stringify({ xml }),
      }),
    );
  },

  async createSampleDisclosure(): Promise<DisclosureView> {
    return normalizeDisclosure(await request("/api/disclosures/sample", { method: "POST" }));
  },

  async saveDisclosure(id: string, file: LoanFile): Promise<DisclosureView> {
    return normalizeDisclosure(
      await request(`/api/disclosures/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ file }),
      }),
    );
  },

  async assembleDisclosure(id: string): Promise<DisclosureView> {
    return normalizeDisclosure(
      await request(`/api/disclosures/${encodeURIComponent(id)}/assemble`, { method: "POST" }),
    );
  },

  async sendDisclosure(id: string): Promise<DisclosureView> {
    return normalizeDisclosure(
      await request(`/api/disclosures/${encodeURIComponent(id)}/send`, { method: "POST" }),
    );
  },

  async downloadDisclosure(id: string, kind: "package" | "xml", filename: string): Promise<void> {
    const path =
      kind === "xml"
        ? `/api/disclosures/${encodeURIComponent(id)}/xml`
        : `/api/disclosures/${encodeURIComponent(id)}/package`;
    await downloadFile(path, filename);
  },

  disclosurePackageBlob(id: string): Promise<Blob> {
    return fetchBlob(`/api/disclosures/${encodeURIComponent(id)}/package`);
  },

  async submitApplication(file: LoanFile): Promise<{
    id: string;
    reference: string;
    xml: string;
    message: string;
  }> {
    const raw = await request<any>("/api/apply", {
      method: "POST",
      body: JSON.stringify({ file }),
    });
    return {
      id: text(value(raw, "id")),
      reference: text(value(raw, "reference")),
      xml: text(value(raw, "xml")),
      message: text(value(raw, "message"), "Application received."),
    };
  },
};

function normalizeDisclosureItem(input: any): DisclosureListItem {
  const status = text(value(input, "status"), "review");
  return {
    id: text(value(input, "id")),
    status: isDisclosureStatus(status) ? status : "review",
    source: text(value(input, "source")),
    loan_number: text(value(input, "loan_number", "loanNumber")),
    borrower_name: text(value(input, "borrower_name", "borrowerName")),
    purpose: text(value(input, "purpose")),
    borrower_mode: text(value(input, "borrower_mode", "borrowerMode")),
    property_city: text(value(input, "property_city", "propertyCity")),
    property_street: text(value(input, "property_street", "propertyStreet")),
    updated_at: text(value(input, "updated_at", "updatedAt")),
  };
}

function isDisclosureStatus(status: string): status is DisclosureStatus {
  return (
    status === "submitted" || status === "review" || status === "assembled" || status === "sent"
  );
}

function normalizeDisclosure(input: any): DisclosureView {
  const raw = entity(input, "disclosure") ?? input;
  const file = value(raw, "file") ?? {};
  return {
    id: text(value(raw, "id")) || undefined,
    status: isDisclosureStatus(text(value(raw, "status")))
      ? (text(value(raw, "status")) as DisclosureStatus)
      : undefined,
    source: text(value(raw, "source")) || undefined,
    file,
    holes: list(raw, "holes").map((hole: any) => ({
      key: text(value(hole, "key")),
      label: text(value(hole, "label")),
      blocks_send: bool(value(hole, "blocks_send", "blocksSend")),
    })),
    forms: list(raw, "forms").map((form: any) => ({
      id: text(value(form, "id")),
      title: text(value(form, "title")),
      audience: text(value(form, "audience"), "shared") as DisclosureFormLine["audience"],
    })),
    metrics: {
      ltv: text(value(value(raw, "metrics"), "ltv"), "n/a"),
      monthly_pi: text(value(value(raw, "metrics"), "monthly_pi", "monthlyPi")),
      proposed_housing: text(value(value(raw, "metrics"), "proposed_housing", "proposedHousing")),
      monthly_income: text(value(value(raw, "metrics"), "monthly_income", "monthlyIncome")),
      front_end_dti: text(value(value(raw, "metrics"), "front_end_dti", "frontEndDti"), "n/a"),
      closing_costs: text(value(value(raw, "metrics"), "closing_costs", "closingCosts")),
      cash_to_close: text(value(value(raw, "metrics"), "cash_to_close", "cashToClose")),
      points_amount: text(value(value(raw, "metrics"), "points_amount", "pointsAmount")),
      borrower_mode: text(value(value(raw, "metrics"), "borrower_mode", "borrowerMode")),
      draft: bool(value(value(raw, "metrics"), "draft")),
    },
    signers: list(raw, "signers").map((signer: any) => ({
      role: text(value(signer, "role")),
      name: text(value(signer, "name")),
      email: text(value(signer, "email")),
    })),
    invite: {
      subject: text(value(value(raw, "invite"), "subject")),
      message: text(value(value(raw, "invite"), "message")),
    },
    signnow_configured: bool(value(raw, "signnow_configured", "signnowConfigured")),
    signnow_from_email: text(value(raw, "signnow_from_email", "signnowFromEmail")) || undefined,
    package_ready: bool(value(raw, "package_ready", "packageReady")),
    signnow_document_id: text(value(raw, "signnow_document_id", "signnowDocumentId")) || undefined,
    sent_at: text(value(raw, "sent_at", "sentAt")) || undefined,
    created_at: text(value(raw, "created_at", "createdAt")) || undefined,
    updated_at: text(value(raw, "updated_at", "updatedAt")) || undefined,
  };
}

async function fetchBlob(path: string): Promise<Blob> {
  loadPersistedAuth();
  const headers = new Headers();
  headers.set("Accept", "application/pdf, application/xml, */*");
  if (sessionToken) {
    headers.set("Authorization", `Bearer ${sessionToken}`);
  }
  const response = await fetch(apiUrl(path), { headers, credentials: FETCH_CREDENTIALS });
  if (!response.ok) {
    throw new ApiError(`Download failed (${response.status}).`, response.status);
  }
  return response.blob();
}

async function downloadFile(path: string, filename: string): Promise<void> {
  const blob = await fetchBlob(path);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const apiInternals = {
  normalizeConditionProject,
  normalizePayroll,
  normalizeProject,
};
